import { FormType, Rate, ShortForm } from '@lenderful/domain';
import { FieldNames } from 'app/models/fields/names';
import { getLoanAmount, Endpoints, getSalesforceParams } from 'app/api/helpers';
import { LoanPurpose } from 'app/api/rates/types';
import { client } from 'app/api/_client';
import { RateInfo } from 'app/models/types';
import { logger } from 'app/util/logger';
import { LoanOfficerDetail } from 'app/api/loan-officer';
import { isTakingCashOut } from 'app/models/fields/conditionals';
import { ShortFormRequestBody } from 'app/api/short-form/types';
import { HomeEquityRatesResponse } from 'app/api/home-equity/types';
import { toSqlDate } from '../loan-application/mappings';

/**
 * Takes in the rates from the forms and maps them over to the payload
 * expected by the shortform lambda
 *
 * @param {RateInfo[]} [rates=[]]
 * @returns {Rate[]}
 */
export const mapRatesToPayload = (rates: RateInfo[] = []): Rate[] => {
  return rates.map((rate) => {
    return {
      type   : rate.loanType,
      payment: rate.principalAndInterestPayment,
      rate   : rate.rate,
      apr    : rate.apr,
      fees   : rate.quoteFees,
      link   : rate.applyUrl,
    };
  });
};

/**
 * Takes in the home equity rates from the forms and maps them over to the payload
 * expected by the shortform lambda
 *
 * @param {RateInfo[]} [rates=[]]
 * @returns {Rate[]}
 */
export const mapHomeEquityRatesToPayload = (homeEquityResponse: HomeEquityRatesResponse): Rate[] => {
  const rates = [...homeEquityResponse.fixedRatePrograms];
  if (homeEquityResponse.lineOfCredit) {
    rates.push(homeEquityResponse.lineOfCredit.program);
  }
  return rates.map((rate) => {
    return {
      type   : rate.productFamily === 'HELOC' ? 'Line of Credit' : rate.loanType,
      payment: rate.principalAndInterestPayment,
      rate   : rate.rate,
      apr    : rate.apr,
      fees   : 0,
      link   : '',
    };
  });
};

/**
 * Maps over form values and reassigns them a key recognized by the
 * ShortForm lambda.
 *
 * @param {any} form values from form
 * @param {LoanOfficerDetail} loanOfficer
 * @param {string} avmReport
 * @returns {ShortForm.RefinanceApplication | ShortForm.PurchaseApplication | ShortForm.HomeEquityTurboApplication} Payload accepted by the API
 */
export const mapUserDataToPayload = (form, loanOfficer: LoanOfficerDetail, avmReportId?: number): ShortForm.PurchaseApplication | ShortForm.RefinanceApplication | ShortForm.HomeEquityTurboApplication => {
  const clientMeta = {
    isAffinityProgram : form[FieldNames.isAffinityProgram],
    loanOfficerId     : loanOfficer.id,
    sf                : { ...getSalesforceParams() },
  };
  const payload: ShortForm.PurchaseApplication | ShortForm.RefinanceApplication | ShortForm.HomeEquityTurboApplication | ShortForm.RefinanceTurboApplication | ShortForm.PurchaseTurboApplication = {
    avm                        : { avmReportId },
    client_meta                : clientMeta,
    branch_employee            : form[FieldNames.branchEmployee],
    branch_number              : form[FieldNames.branchNumber],
    buying_stage               : form[FieldNames.buyingProcess],
    credit_score               : form[FieldNames.creditScore],
    current_interest_rate      : form[FieldNames.currentInterestRate],
    current_loan_term          : form[FieldNames.currentLoanTerm],
    current_loan_type          : form[FieldNames.currentLoanType],
    current_monthly_payment    : form[FieldNames.currentMonthlyPayment],
    dateOfBirth                : toSqlDate(form[FieldNames.dateOfBirth]),
    down_payment               : form[FieldNames.downDollar],
    email                      : form[FieldNames.email],
    employerName               : form[FieldNames.employCompanyName],
    estimated_home_value       : form[FieldNames.homeValue],
    expectedClosingDate        : toSqlDate(form[FieldNames.expectedClosingDate]),
    firstName                  : form[FieldNames.firstName],
    flag_cash_out              : isTakingCashOut(form) ? 'Yes' : 'No',
    flag_escro_taxes           : form[FieldNames.escrowUsage],
    hasUniqueHomeEquityProgram : form[FieldNames.hasUniqueHomeEquityProgram],
    lastName                   : form[FieldNames.lastName],
    loan_amount                : getLoanAmount(form),
    phone                      : form[FieldNames.phone],
    propertyCity               : form[FieldNames.propertyCity],
    propertyState              : form[FieldNames.propertyState],
    propertyStreet             : form[FieldNames.propertyStreet],
    property_type              : form[FieldNames.propertyType],
    property_use               : form[FieldNames.propertyUsage],
    purchase_price             : form[FieldNames.homeValue],
    purpose_of_refinance       : form[FieldNames.homeEquityLoanPurpose] ? form[FieldNames.homeEquityLoanPurpose] : form[FieldNames.loanPurpose],
    type                       : LoanPurpose[form[FieldNames.loanPurpose]],
    unpaid_balance_amount      : form[FieldNames.mortgageBalance],
    yearBuilt                  : form[FieldNames.builtYear],
    zip_code                   : form[FieldNames.propertyZip],
  };

  return payload;
};

const getShortFormTransactionType = (formName: FormType): ShortForm.TransactionType => {
  switch (formName) {
    case FormType.SHORT_FORM_PURCHASE:
      return ShortForm.TransactionType.PURCHASE;
    case FormType.SHORT_FORM_REFINANCE:
      return ShortForm.TransactionType.REFINANCE;
    case FormType.REVERSE:
      return ShortForm.TransactionType.REVERSE;
    case FormType.HOME_EQUITY:
      return ShortForm.TransactionType.HOME_EQUITY;
    case FormType.HOME_EQUITY_TURBO:
      return ShortForm.TransactionType.HOME_EQUITY_TURBO;
    case FormType.HOME_EQUITY_TURBO_WELCOME:
      return ShortForm.TransactionType.HOME_EQUITY_TURBO;
    case FormType.REFINANCE_TURBO:
      return ShortForm.TransactionType.REFINANCE_TURBO;
    case FormType.PURCHASE_TURBO:
      return ShortForm.TransactionType.PURCHASE_TURBO;
    default:
      return undefined;
  }
};
/**
 * This method posts to the backend endpoint for short forms
 * (purchase, refi, home-equity).
 *
 * @param {ShortFormRequestBody} body
 * @returns {Promise<void>}
 */
export const postShortForm = async (body: ShortFormRequestBody): Promise<void> => {
  const transaction = getShortFormTransactionType(body.formName);
  return client
    .post(`${Endpoints.SHORTFORM}/${transaction}`, body)
    .then((response) => {
      if (response.data?.errorMessage) {
        logger.error('Error in Posting short form: ', response);
        throw response;
      } else {
        return response.data;
      }
    })
    .catch((error) => {
      logger.error('Error in Posting short form: ', error);
      throw error;
    });
};
